﻿import { useState, useEffect } from 'react';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
// Cornerstone/DICOM viewer temporarily disabled — too slow to build (see TE-27).
// import DicomViewer from '../../components/viewers/DicomViewer';
import { Patient as PatientType, Insurance } from '../../types';

import { FaPersonCane } from "react-icons/fa6";

const Patient = () => {
	const { id } = useParams<{ id: string }>();
	const { token } = useAuth();
	const [patient, setPatient] = useState<PatientType | null>(null);
	const [loading, setLoading] = useState(true);

	const [activeTab, setActiveTab] = useState('overview');

	const [documents, setDocuments] = useState([]);
	const [correspondence, setCorrespondence] = useState([]);

	// Modal States
	const [showAddInsuranceModal, setShowAddInsuranceModal] = useState(false);
	const [showEditInsuranceModal, setShowEditInsuranceModal] = useState(false);
	const [showAddReferralModal, setShowAddReferralModal] = useState(false);
	const [showAddAdminNoteModal, setShowAddAdminNoteModal] = useState(false);
	const [currentInsurance, setCurrentInsurance] = useState<Insurance | null>(null);

	console.log('====== RENDER: Patient Component with ID:', id, '======');

	// Function to fetch patient data
	const fetchPatient = async () => {
		try {
			console.log(`🔍 Fetching patient with UUID: ${id}`);
			const response = await apiCall(`/patients/${id}/`, { method: 'GET' }, token || undefined);
			
			if (response.ok) {
				const data = await response.json();
				console.log('📊 Fetched patient data:', JSON.stringify(data));
				console.log('✅ Patient found!', data);
				setPatient(data);
			} else {
				console.error('❌ No patient found with UUID:', id);
			}
			setLoading(false);
		} catch (error) {
			console.error('❌ Error fetching patient:', error);
			setLoading(false);
		}
	};

	useEffect(() => {
		// Reset state when patient ID changes
		console.log('🔄 Effect triggered with ID:', id);
		setPatient(null);
		setLoading(true);
		
		fetchPatient();

		// Cleanup function
		return () => {
			console.log('🧹 Cleanup for ID:', id);
		};
	}, [id]); // Re-run the effect when id changes

	// Debug log whenever patient state changes
	useEffect(() => {
		console.log('👤 Patient state updated:', patient);
	}, [patient]);

	// Fetch documents for the patient
	useEffect(() => {
		const fetchDocuments = async () => {
			if (!patient?.id || !token) return;
			
			try {
				console.log('📄 Fetching documents for patient:', patient.id);
				const response = await apiCall(
					`/documents/documents/?patient_id=${patient.id}`,
					{ method: 'GET' },
					token
				);
				
				if (response.ok) {
					const data = await response.json();
					const docs = data.results || data || [];
					console.log('📄 Documents fetched:', docs.length, 'documents');
					setDocuments(docs);
				} else {
					console.error('❌ Failed to fetch documents');
					setDocuments([]);
				}
			} catch (error) {
				console.error('❌ Error fetching documents:', error);
				setDocuments([]);
			}
		};
		
		fetchDocuments();
	}, [patient?.id, token]);

	// Fetch correspondence for the patient
	useEffect(() => {
		const fetchCorrespondence = async () => {
			if (!patient?.id || !token) return;
			
			try {
				console.log('✉️ Fetching correspondence for patient:', patient.id);
				const response = await apiCall(
					`/correspondence/letters/?patient_id=${patient.id}`,
					{ method: 'GET' },
					token
				);
				
				if (response.ok) {
					const data = await response.json();
					const letters = data.results || data || [];
					console.log('✉️ Correspondence fetched:', letters.length, 'letters');
					setCorrespondence(letters);
				} else {
					console.error('❌ Failed to fetch correspondence');
					setCorrespondence([]);
				}
			} catch (error) {
				console.error('❌ Error fetching correspondence:', error);
				setCorrespondence([]);
			}
		};
		
		fetchCorrespondence();
	}, [patient?.id, token]);

	if (loading) {
		console.log('⏳ Rendering loading state...');
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
				<div className="ml-4 text-xl font-semibold">Loading patient data...</div>
			</div>
		);
	}

	if (!patient) {
		console.log('⚠️ Rendering patient not found state...');
		return <div className="p-10 text-xl text-center text-error">Patient not found</div>;
	}

	console.log('🎨 Rendering patient details for:', patient.first_name, patient.last_name, '(ID:', patient.display_id, ')');
	
	const formatDate = (dateStr: string) => {
		return new Date(dateStr).toLocaleDateString('en-AU', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
	};

	const formatPhone = (phone: string | undefined) => {
		if (!phone) return '';
		// Format Australian mobile numbers
		return phone.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1 $2 $3');
	};

	const deriveStateFromPostcode = (postcode: string | undefined) => {
		const pc = Number((postcode || '').trim());
		if (!Number.isFinite(pc)) return '';

		if ((pc >= 1000 && pc <= 1999) || (pc >= 2000 && pc <= 2599) || (pc >= 2619 && pc <= 2899) || (pc >= 2921 && pc <= 2999)) return 'NSW';
		if ((pc >= 200 && pc <= 299) || (pc >= 2600 && pc <= 2618) || (pc >= 2900 && pc <= 2920)) return 'ACT';
		if ((pc >= 3000 && pc <= 3999) || (pc >= 8000 && pc <= 8999)) return 'VIC';
		if ((pc >= 4000 && pc <= 4999) || (pc >= 9000 && pc <= 9999)) return 'QLD';
		if (pc >= 5000 && pc <= 5799) return 'SA';
		if ((pc >= 6000 && pc <= 6797) || (pc >= 6800 && pc <= 6999)) return 'WA';
		if (pc >= 7000 && pc <= 7999) return 'TAS';
		if (pc >= 800 && pc <= 999) return 'NT';

		return '';
	};

	const getStateDisplay = () => {
		const stateValue = ((patient as any)?.state || '').toString().trim().toUpperCase();
		if (stateValue) return stateValue;

		const stateIdValue = (patient?.state_id || '').toString().trim();
		if (stateIdValue && /^[A-Z]{2,3}$/.test(stateIdValue.toUpperCase())) {
			return stateIdValue.toUpperCase();
		}

		return deriveStateFromPostcode(patient?.postal_code);
	};

	// Get primary next of kin
	const primaryNextOfKin = patient.next_of_kin?.find(nok => nok.is_primary) || patient.next_of_kin?.[0];
	
	// Get highest priority emergency contact
	const primaryEmergencyContact = patient.emergency_contacts?.sort((a, b) => a.priority - b.priority)[0];
	
	// Get primary referral (most recent active one)
	const primaryReferral = patient?.referrals?.sort((a, b) => 
		new Date(b.referral_date).getTime() - new Date(a.referral_date).getTime()
	)?.find(ref => ref.status === 'valid') || patient?.referrals?.[0];

	// Helper functions for insurance and referral management
	const handleAddInsurance = () => {
		setCurrentInsurance(null);
		setShowAddInsuranceModal(true);
	};

	const handleEditInsurance = (insurance: Insurance) => {
		setCurrentInsurance(insurance);
		setShowEditInsuranceModal(true);
	};

	const handleAddSpecialInsurance = (type: string) => {
		setCurrentInsurance({
			id: undefined,
			patient: patient?.id || 0,
			insurance_type: type,
			provider_name: type === 'tac' ? 'Transport Accident Commission' : 
				type === 'workcover' ? 'WorkCover' : 
				type === 'dva' ? 'Department of Veterans Affairs' : '',
			policy_number: '',
			membership_number: '',
			expiry_date: '',
			is_primary: false,
			notes: ''
		});
		setShowAddInsuranceModal(true);
	};

	const handleDeleteInsurance = async (insuranceId: number | string) => {
		if (window.confirm('Are you sure you want to delete this insurance record?')) {
			try {
				await apiCall(`/insurance/${insuranceId}/`, { method: 'DELETE' }, token || undefined);
				// Refresh patient data
				fetchPatient();
			} catch (error) {
				console.error('Error deleting insurance:', error);
				alert('Failed to delete insurance record.');
			}
		}
	};

	const handleAddReferral = () => {
		setShowAddReferralModal(true);
	};

	const handleAddAdminNote = () => {
		setShowAddAdminNoteModal(true);
	};

	const stateDisplay = getStateDisplay();

	// Main Layout - Now wrapped in Syncfusion structure
	return (
		<div className="bg-base-200" style={{ height: 'calc(100vh - 140px)' }}>
			{/* Main Content */}
			<div className="overflow-y-auto">
				<div className="h-full">
					<div className="border-b">
							{/* Top Row: Patient Info & Side Boxes */}
							<div className="items-start gap-4">
								{/* Left Column - Patient Info & Insurance */}
								<div className="border border-base-300 rounded-lg p-4 h-full">
									<h2 className="text-xl font-bold">TEST {patient.first_name} {patient.last_name}</h2>
									<div className="mt-1 text-sm space-y-0.5">
										<p>ID: {patient.display_id} | DOB: {formatDate(patient.date_of_birth)} ({patient.age}y) | {patient.gender === 'M' ? 'Male' : patient.gender === 'F' ? 'Female' : patient.gender}</p>
										
										{/* Medicare Details */}
										{patient.medicare && (
											<p>Medicare: {patient.medicare.medicare_number}-{patient.medicare.reference_number} (Exp: {formatDate(patient.medicare.expiry_date)})</p>
										)}
										
										{/* Next of Kin & Emergency Contact */}
										<p>
											{primaryNextOfKin && (
												<>Next of Kin: {primaryNextOfKin.first_name} {primaryNextOfKin.last_name} ({primaryNextOfKin.relationship_display || primaryNextOfKin.relationship})<br /></>
											)}
											
											{primaryEmergencyContact && (
												<>Emergency Contact: {primaryEmergencyContact.mobile || primaryEmergencyContact.phone || 'No phone'}</>
											)}
										</p>
									</div>

									{/* Insurance Information Boxes */}
									<div className="flex gap-2 mt-3">
										{patient.insurance && patient.insurance.map((insurance, index) => (
											<div key={insurance.id || index} className={`px-3 py-2 ${getInsuranceColor(insurance.insurance_type).bg} border ${getInsuranceColor(insurance.insurance_type).border} rounded-lg`}>
												<div className="flex items-center">
													<span className={`w-2 h-2 ${getInsuranceColor(insurance.insurance_type).dot} rounded-full mr-2`}></span>
													<span className={`text-xs font-medium ${getInsuranceColor(insurance.insurance_type).text}`}>
														{insurance.insurance_type_display || insurance.insurance_type}
													</span>
												</div>
											</div>
										))}

										{(!patient.insurance || patient.insurance.length === 0) && (
											<div className="px-3 py-2 bg-base-200 border border-base-300 rounded-lg">
												<div className="flex items-center">
													<span className="w-2 h-2 bg-base-content opacity-40 rounded-full mr-2"></span>
													<span className="text-xs font-medium text-base-content opacity-80">No Insurance</span>
												</div>
											</div>
										)}
									</div>
								</div>

								{/* Contact Information Grid */}
								<div className="grid grid-cols-2 gap-2 w-96 h-full rounded-lg shadow-sm border border-base-300">
									<div className="p-2 rounded-lg shadow-sm border border-base-300">
										<h3 className="text-xs font-medium">Contact Details</h3>
										<div className="space-y-0.5">
											<p className="text-xs">Email: {patient.contact_email || 'Not provided'}</p>
											<p className="text-xs">Mobile: {formatPhone(patient.contact_phone) || 'Not provided'}</p>
										</div>
									</div>

									<div className="p-2 rounded-lg shadow-sm border border-base-300">
										<h3 className="text-xs font-medium mb-1">Residential Address</h3>
										<p className="text-xs">{patient.address_line1 || 'No address line 1'}</p>
										<p className="text-xs">
											{patient.address_line2 ? `${patient.address_line2}, ` : ''}
											{patient.city ? `${patient.city}${stateDisplay ? ` ${stateDisplay}` : ''}${patient.postal_code ? ` ${patient.postal_code}` : ''}` : 'No city/postal code'}
										</p>
									</div>

									<div className="p-2 rounded-lg shadow-sm border border-base-300">
										<h3 className="text-xs font-medium mb-1">Referrer</h3>
										{primaryReferral ? (
											<>
												<p className="text-xs">{primaryReferral.referrer_name}</p>
												{primaryReferral.is_valid ? (
													<p className="text-xs text-success">Valid ({primaryReferral.days_remaining} days remaining)</p>
												) : (
													<p className="text-xs text-error">Referral Expired</p>
												)}
											</>
										) : (
											<p className="text-xs">No referrer on record</p>
										)}
									</div>
								</div>

								{/* Outcome Measures Box */}
								<div className="w-96 h-full rounded-lg shadow-sm border border-base-300 p-3">
									<div className="flex justify-between items-center mb-2">
										<h3 className="text-sm font-medium">Outcome Measures</h3>
										<button className="text-xs hover:text-primary">Assign New</button>
									</div>
									<table className="w-full">
										<thead>
											<tr>
												<th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Date</th>
												<th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Measure</th>
												<th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Status</th>
											</tr>
										</thead>
										<tbody className="text-xs">
											{patient.outcome_measures && patient.outcome_measures.length > 0 ? (
												patient.outcome_measures.map((measure) => (
													<tr key={measure.id}>
														<td className="px-2 py-1">{formatDate(measure.assigned_date)}</td>
														<td className="px-2 py-1">{measure.title}</td>
														<td className="px-2 py-1">
															<span className={`inline-flex items-center ${getStatusColor(measure.status).text}`}>
																{getStatusIcon(measure.status)}
																{measure.status_display || capitalize(measure.status)}
															</span>
														</td>
													</tr>
												))
											) : (
												<tr>
													<td colSpan={3} className="px-2 py-1 text-center text-base-content opacity-60">No outcome measures assigned</td>
												</tr>
											)}
										</tbody>
									</table>
								</div>

								{/* Admin Notes Box */}
								<div className="w-96 h-full rounded-lg shadow-sm border border-base-300 p-3">
									<div className="flex justify-between items-center mb-2">
										<h3 className="text-sm font-medium text-base-content">Admin Notes</h3>
										<button 
											className="text-xs hover:text-primary"
											onClick={handleAddAdminNote}
										>
											Add Note
										</button>
									</div>
									<div className="space-y-2">
										{patient.admin_notes && patient.admin_notes.length > 0 ? (
											patient.admin_notes.map((note) => (
												<div key={note.id} className="border-l-2 border-blue-500 pl-2">
													<p className="text-xs">{note.title}</p>
													<p className="text-xs">{note.content}</p>
													<p className="text-xs">Added by {note.created_by_name || 'User'} - {formatDate(note.created_at)}</p>
												</div>
											))
										) : (
											<div className="border-l-2 border-base-300 pl-2">
												<p className="text-xs text-gray-500">No admin notes available</p>
											</div>
										)}
									</div>
								</div>
							</div>

							{/* Critical Information Banner */}
							<div className="mt-4 p-3 bg-error/10 border-l-4 border-error">
								<div className="flex items-start">
									<div className="flex-shrink-0">
										<svg className="h-5 w-5 text-error" viewBox="0 0 20 20" fill="currentColor">
											<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
										</svg>
									</div>
									<div className="ml-3 flex-1">
										<h3 className="text-sm font-medium text-error inline">Critical Patient Information:</h3>
										<span className="text-sm text-error opacity-90 ml-2">Allergies: Penicillin, NSAIDs | Blood Thinners: Warfarin (Last INR: 2.8 on 08/11/2024) | DNR Status: Full Code | Recent ER Visit: 02/11/2024 - Acute Pain Crisis</span>
									</div>
								</div>
							</div>
						</div>
						{/* Main Content Area with Tabs */}
						<div className="w-full px-2">
							<div className="mb-4 border-b border-base-300">
								<ul className="flex flex-wrap -mb-px text-sm font-medium text-center justify-center" role="tablist">
									<li className="mr-2" role="presentation">
										<button
											className={`inline-block p-2 border-b-2 ${activeTab === 'overview'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('overview')}
										><FaPersonCane />
											Overview
										</button>
									</li>
									<li className="mr-2" role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'clinical'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('clinical')}
										>
											Clinical
										</button>
									</li>
									<li className="mr-2" role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'imaging'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('imaging')}
										>
											Imaging & Results
										</button>
									</li>
									<li className="mr-2" role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'documents'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('documents')}
										>
											Documents
										</button>
									</li>
									<li className="mr-2" role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'correspondence'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('correspondence')}
										>
											Correspondence
										</button>
									</li>
									<li className="mr-2" role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'billing'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('billing')}
										>
											Billing
										</button>
									</li>
									<li role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'admin'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('admin')}
										>
											Administrative
										</button>
									</li>

									<li role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'referrals'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('referrals')}
										>
											Referrals
										</button>
									</li>

									<li role="presentation">
										<button
											className={`inline-block p-4 border-b-2 rounded-t-lg ${activeTab === 'insurance'
												? 'text-blue-600 border-blue-600 dark:text-blue-500 dark:border-blue-500'
												: 'hover:text-base-content opacity-70 hover:border-base-300 dark:hover:text-base-content opacity-50'
												}`}
											onClick={() => setActiveTab('insurance')}
										>
											Insurance
										</button>
									</li>
								</ul>
							</div>

							{/* Tab Content Area */}
							<div className=" rounded-lg shadow-md p-2">
								{/* Overview Tab Content */}
								{activeTab === 'overview' && (
									<div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">

										{/* Current Pain Status */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-lg font-semibold text-base-content">Current Pain Status</h3>
												<span className="text-sm text-base-content opacity-60">Updated 2 hours ago</span>
											</div>
											<div className="space-y-4">
												<div className="flex items-center justify-between">
													<span className="text-sm font-medium text-base-content opacity-70 dark:text-base-content opacity-50">Pain Score:</span>
													<div className="flex items-center">
														<span className="text-xl font-bold text-red-600 dark:text-red-400">7</span>
														<span className="text-sm text-base-content opacity-60">/10</span>
													</div>
												</div>
												<div>
													<span className="text-sm font-medium text-base-content opacity-70 dark:text-base-content opacity-50">Location:</span>
													<p className="mt-1 text-sm text-base-content">Lower back (L4-L5), radiating to left leg</p>
												</div>
												<div>
													<span className="text-sm font-medium text-base-content opacity-70 dark:text-base-content opacity-50">Quality:</span>
													<p className="mt-1 text-sm text-base-content">Sharp, burning, worse with movement</p>
												</div>
												<div>
													<span className="text-sm font-medium text-base-content opacity-70 dark:text-base-content opacity-50">Aggravating Factors:</span>
													<p className="mt-1 text-sm text-base-content">Prolonged sitting, bending, morning stiffness</p>
												</div>
												<div>
													<span className="text-sm font-medium text-base-content opacity-70 dark:text-base-content opacity-50">Relieving Factors:</span>
													<p className="mt-1 text-sm text-base-content">Heat therapy, prescribed medications, lying flat</p>
												</div>
												<button className="mt-4 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
													Update Pain Assessment
												</button>
											</div>
										</div>

										{/* Active Medications */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-lg font-semibold text-base-content">Active Medications</h3>
												<button className="text-sm text-blue-600 hover:text-primary dark:text-blue-400">Add New</button>
											</div>
											<div className="space-y-4">
												<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
													<div className="flex items-start">
														<div className="flex-1">
															<h4 className="text-sm font-medium text-base-content">Pregabalin (Lyrica)</h4>
															<p className="text-sm text-base-content opacity-70">75mg BID</p>
															<p className="text-xs text-gray-500">Last renewed: 01/11/2024</p>
														</div>
														<span className="px-2 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full">
															Review Due
														</span>
													</div>
												</div>
												<div className="p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex items-start">
														<div className="flex-1">
															<h4 className="text-sm font-medium text-base-content">Oxycodone IR</h4>
															<p className="text-sm text-base-content opacity-70">10mg Q6H PRN</p>
															<p className="text-xs text-gray-500">Last dispensed: 05/11/2024</p>
														</div>
														<span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
															Active
														</span>
													</div>
												</div>

												<div className="p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex items-start">
														<div className="flex-1">
															<h4 className="text-sm font-medium text-base-content">Duloxetine (Cymbalta)</h4>
															<p className="text-sm text-base-content opacity-70">60mg Daily</p>
															<p className="text-xs text-gray-500">Last renewed: 15/10/2024</p>
														</div>
														<span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
															Active
														</span>
													</div>
												</div>
											</div>
										</div>

										{/* Upcoming Appointments/Procedures */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-lg font-semibold text-base-content">Upcoming Appointments/Procedures</h3>
												<button className="text-sm text-blue-600 hover:text-primary dark:text-blue-400">Schedule New</button>
											</div>
											<div className="space-y-3">
												<div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
													<div className="flex-1">
														<p className="text-sm font-medium text-base-content">Pain Review</p>
														<p className="text-sm text-base-content opacity-70">15/11/2024 - 10:30 AM</p>
														<p className="text-xs text-gray-500">With: Dr. Smith</p>
													</div>
													<div className="flex space-x-2">
														<button className="px-2 py-1 text-xs font-medium text-blue-700 hover:text-blue-800">
															Edit
														</button>
														<button className="px-2 py-1 text-xs font-medium text-red-700 hover:text-red-800">
															Cancel
														</button>
													</div>
												</div>

												<div className="flex items-center p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex-1">
														<p className="text-sm font-medium text-base-content">Physical Therapy</p>
														<p className="text-sm text-base-content opacity-70">18/11/2024 - 2:00 PM</p>
														<p className="text-xs text-gray-500">With: Jane Wilson, PT</p>
													</div>
													<div className="flex space-x-2">
														<button className="px-2 py-1 text-xs font-medium text-blue-700 hover:text-blue-800">
															Edit
														</button>
														<button className="px-2 py-1 text-xs font-medium text-red-700 hover:text-red-800">
															Cancel
														</button>
													</div>
												</div>

												<div className="flex items-center p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex-1">
														<p className="text-sm font-medium text-base-content">MRI Lumbar Spine</p>
														<p className="text-sm text-base-content opacity-70">22/11/2024 - 9:15 AM</p>
														<p className="text-xs text-gray-500">At: City Imaging Center</p>
													</div>
													<div className="flex space-x-2">
														<button className="px-2 py-1 text-xs font-medium text-blue-700 hover:text-blue-800">
															Edit
														</button>
														<button className="px-2 py-1 text-xs font-medium text-red-700 hover:text-red-800">
															Cancel
														</button>
													</div>
												</div>
											</div>
										</div>
										{/* Active Diagnoses */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-lg font-semibold text-base-content">Active Diagnoses</h3>
												<button className="text-sm text-blue-600 hover:text-primary dark:text-blue-400">Add Diagnosis</button>
											</div>
											<div className="space-y-3">
												<div className="p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex items-start justify-between">
														<div>
															<h4 className="text-sm font-medium text-base-content">Chronic Lower Back Pain</h4>
															<p className="text-xs text-base-content opacity-70">ICD-10: M54.5</p>
															<p className="text-xs text-gray-500">Onset: 06/2024</p>
														</div>
														<span className="px-2 py-1 text-xs font-medium text-red-800 bg-red-100 rounded-full">
															Primary
														</span>
													</div>
													<p className="mt-2 text-sm text-base-content opacity-70">
														Notes: Persistent pain following work injury. Failed conservative management.
													</p>
												</div>

												<div className="p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex items-start justify-between">
														<div>
															<h4 className="text-sm font-medium text-base-content">Lumbar Radiculopathy</h4>
															<p className="text-xs text-base-content opacity-70">ICD-10: M54.16</p>
															<p className="text-xs text-gray-500">Onset: 08/2024</p>
														</div>
														<span className="px-2 py-1 text-xs font-medium text-orange-800 bg-orange-100 rounded-full">
															Secondary
														</span>
													</div>
													<p className="mt-2 text-sm text-base-content opacity-70">
														Notes: Left-sided L4-L5 radiculopathy, confirmed on EMG
													</p>
												</div>

												<div className="p-3 bg-base-100 border border-base-300 rounded-lg">
													<div className="flex items-start justify-between">
														<div>
															<h4 className="text-sm font-medium text-base-content">Major Depressive Disorder</h4>
															<p className="text-xs text-base-content opacity-70">ICD-10: F32.9</p>
															<p className="text-xs text-gray-500">Onset: 09/2024</p>
														</div>
														<span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
															Managed
														</span>
													</div>
													<p className="mt-2 text-sm text-base-content opacity-70">
														Notes: Secondary to chronic pain. Under care of Dr. Johnson (Psychiatrist)
													</p>
												</div>
											</div>

											<div className="mt-4 pt-4 border-t border-base-300">
												<h4 className="text-sm font-medium text-base-content mb-2">Past Medical History</h4>
												<ul className="list-disc pl-5 text-sm text-base-content opacity-70 space-y-1">
													<li>Hypertension (controlled)</li>
													<li>Type 2 Diabetes (diet-controlled)</li>
													<li>Appendectomy (2010)</li>
												</ul>
											</div>
										</div>

										{/* Recent Correspondence */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-lg font-semibold text-base-content">Recent Correspondence</h3>
												<div className="flex space-x-2">
													<button className="text-sm text-blue-600 hover:text-primary dark:text-blue-400">New Letter</button>
													<button className="text-sm text-blue-600 hover:text-primary dark:text-blue-400">View All</button>
												</div>
											</div>
											<div className="space-y-3">
												<div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
													<div className="flex items-start">
														<div className="flex-1">
															<p className="text-sm font-medium text-base-content">GP Letter (Dr. Anderson)</p>
															<p className="text-sm text-base-content opacity-70">Treatment update and medication changes</p>
															<p className="text-xs text-gray-500">Sent: 08/11/2024 via HealthLink</p>
														</div>
														<button className="px-3 py-1 text-xs font-medium text-blue-700 hover:text-blue-800">
															View
														</button>
													</div>
												</div>

												<div className="p-3 bg-green-50 border border-green-200 rounded-lg">
													<div className="flex items-start">
														<div className="flex-1">
															<p className="text-sm font-medium text-base-content">Physical Therapy Report</p>
															<p className="text-sm text-base-content opacity-70">Progress report and treatment plan</p>
															<p className="text-xs text-gray-500">Received: 05/11/2024</p>
														</div>
														<button className="px-3 py-1 text-xs font-medium text-green-700 hover:text-green-800">
															View
														</button>
													</div>
												</div>

												<div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
													<div className="flex items-start">
														<div className="flex-1">
															<p className="text-sm font-medium text-base-content">Insurance Update</p>
															<p className="text-sm text-base-content opacity-70">Treatment authorization request</p>
															<p className="text-xs text-gray-500">Pending Response: 01/11/2024</p>
														</div>
														<button className="px-3 py-1 text-xs font-medium text-yellow-700 hover:text-yellow-800">
															View
														</button>
													</div>
												</div>
											</div>
										</div>
										{/* Billing Overview */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
											<div className="flex items-center justify-between mb-4">
												<h3 className="text-lg font-semibold text-base-content">Billing Overview</h3>
												<button className="text-sm text-blue-600 hover:text-primary dark:text-blue-400">Full History</button>
											</div>
											<div className="space-y-4">
												<div>
													<div className="flex justify-between items-center mb-2">
														<span className="text-sm font-medium text-base-content opacity-70">Outstanding Balance</span>
														<span className="text-lg font-semibold text-red-600">$450.00</span>
													</div>
													<div className="w-full bg-base-200 rounded-full h-2">
														<div className="bg-red-600 h-2 rounded-full" style={{ width: '45%' }}></div>
													</div>
												</div>

												<div className="border-t border-base-300 pt-4">
													<h4 className="text-sm font-medium text-base-content mb-3">Recent Transactions</h4>
													<div className="space-y-2">
														<div className="flex justify-between items-center">
															<div>
																<p className="text-sm font-medium text-base-content">Consultation</p>
																<p className="text-xs text-gray-500">01/11/2024</p>
															</div>
															<span className="text-sm font-medium text-green-600">-$200.00</span>
														</div>
														<div className="flex justify-between items-center">
															<div>
																<p className="text-sm font-medium text-base-content">Medicare Rebate</p>
																<p className="text-xs text-gray-500">03/11/2024</p>
															</div>
															<span className="text-sm font-medium text-blue-600">+$75.00</span>
														</div>
													</div>
												</div>

												<div className="border-t border-base-300 pt-4">
													<h4 className="text-sm font-medium text-base-content mb-3">Insurance Claims</h4>
													<div className="space-y-2">
														<div className="flex justify-between items-center">
															<div>
																<p className="text-sm font-medium text-base-content">Pending Claims</p>
																<p className="text-xs text-gray-500">2 claims in process</p>
															</div>
															<span className="text-sm font-medium text-yellow-600">$325.00</span>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}
								{/* Clinical Tab Content */}
								{activeTab === 'clinical' && (
									<div className="space-y-6">
										{/* Quick Actions Bar */}
										<div className="flex space-x-4 mb-6">
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
												</svg>
												New Clinical Note
											</button>
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
												
												Generate Report
											</button>
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
												</svg>
												Attach Document
											</button>
										</div>

										{/* Clinical Cards Grid - 2 columns */}
										<div className="grid grid-cols-2 gap-6">

											{/* Active Treatment Plan Card */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<h3 className="text-lg font-semibold text-base-content mb-4">Active Treatment Plan</h3>
												<div className="space-y-4">
													<div className="p-3 bg-green-50 border border-green-200 rounded-lg">
														<h4 className="text-sm font-medium text-base-content">Current Phase: Conservative Management</h4>
														<div className="mt-2">
															<div className="flex justify-between text-xs text-gray-500">
																<span>Progress</span>
																<span>Week 6 of 12</span>
															</div>
															<div className="w-full bg-base-200 rounded-full h-2 mt-1">
																<div className="bg-green-600 h-2 rounded-full" style={{ width: '50%' }}></div>
															</div>
														</div>
													</div>

													{/* Treatment Goals */}
													<div>
														<h4 className="text-sm font-medium text-base-content mb-2">Treatment Goals</h4>
														<div className="space-y-2">
															<div className="flex items-center">
																<input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-base-300" checked />
																<label className="ml-2 text-sm text-base-content opacity-70">Reduce pain score to ≤4/10</label>
															</div>
															<div className="flex items-center">
																<input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-base-300" />
																<label className="ml-2 text-sm text-base-content opacity-70">Improve sleep quality</label>
															</div>
															<div className="flex items-center">
																<input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-base-300" />
																<label className="ml-2 text-sm text-base-content opacity-70">Return to work (modified duties)</label>
															</div>
														</div>
													</div>
												</div>
											</div>

											{/* Clinical Timeline Card */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<h3 className="text-lg font-semibold text-base-content mb-4">Clinical Timeline</h3>
												<div className="space-y-4">
													{/* Today's Visit - Condensed */}
													<div className="flex items-start">
														<div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
															<svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
															</svg>
														</div>
														<div className="ml-4 flex-1">
															<div className="flex justify-between">
																<div>
																	<p className="text-sm font-medium">Follow-up Visit</p>
																	<p className="text-xs text-gray-500">Today - 10:30 AM • Pain Score: 7/10</p>
																</div>
																<button className="text-xs text-blue-600">Edit</button>
															</div>
														</div>
													</div>

													{/* Previous Visits - Condensed */}
													<div className="flex items-start">
														<div className="flex-shrink-0 w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
															<svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
															</svg>
														</div>
														<div className="ml-4 flex-1">
															<div className="flex justify-between">
																<div>
																	<p className="text-sm font-medium">Initial Pain Management</p>
																	<p className="text-xs text-gray-500">01/11/2024 • Started conservative management</p>
																</div>
																<button className="text-xs text-base-content opacity-70">View</button>
															</div>
														</div>
													</div>

													<div className="flex items-start">
														<div className="flex-shrink-0 w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
															<svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
															</svg>
														</div>
														<div className="ml-4 flex-1">
															<div className="flex justify-between">
																<div>
																	<p className="text-sm font-medium">Physical Therapy Assessment</p>
																	<p className="text-xs text-gray-500">25/10/2024 • Home exercise program prescribed</p>
																</div>
																<button className="text-xs text-base-content opacity-70">View</button>
															</div>
														</div>
													</div>

													<button className="w-full text-center text-sm text-blue-600 hover:text-primary font-medium">
														Load More History
													</button>
												</div>
											</div>



											{/* Pain Monitoring Card */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<div className="flex justify-between items-center mb-4">
													<h3 className="text-lg font-semibold text-base-content">Pain Monitoring</h3>
													<button className="text-sm text-blue-600 hover:text-primary">View History</button>
												</div>
												<div className="space-y-4">
													<div className="grid grid-cols-2 gap-2">
														<div className="p-2 bg-gray-50 rounded-lg">
															<p className="text-xs text-gray-500">Current Score</p>
															<p className="text-lg font-medium text-red-600">7/10</p>
														</div>
														<div className="p-2 bg-gray-50 rounded-lg">
															<p className="text-xs text-gray-500">Trend</p>
															<div className="flex items-center text-red-500">
																<span className="text-sm">Increasing</span>
																<svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
																</svg>
															</div>
														</div>
													</div>

													<div className="border-t border-base-300 pt-4">
														<h4 className="text-sm font-medium text-base-content mb-2">Characteristics</h4>
														<div className="grid grid-cols-2 gap-2">
															<div className="p-2 bg-gray-50 rounded-lg">
																<p className="text-xs text-gray-500">Type</p>
																<p className="text-sm font-medium">Sharp, Burning</p>
															</div>
															<div className="p-2 bg-gray-50 rounded-lg">
																<p className="text-xs text-gray-500">Pattern</p>
																<p className="text-sm font-medium">Constant</p>
															</div>
														</div>
													</div>
												</div>
											</div>

											{/* Current Interventions Card */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<h3 className="text-lg font-semibold text-base-content mb-4">Current Interventions</h3>
												<div className="space-y-3">
													<div className="flex items-center">
														<svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
														</svg>
														<span className="text-sm">Physical Therapy - 2x weekly</span>
													</div>
													<div className="flex items-center">
														<svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
														</svg>
														<span className="text-sm">Medication Management</span>
													</div>
													<div className="flex items-center">
														<svg className="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
														</svg>
														<span className="text-sm">Home Exercise Program</span>
													</div>

													<div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
														<h4 className="text-sm font-medium text-base-content">Next Review</h4>
														<p className="text-sm text-base-content opacity-70">15/11/2024 - Treatment Plan Assessment</p>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Imaging & Results Tab Content */}
								{activeTab === 'imaging' && (
									<div className="space-y-6">
										{/* Quick Actions Bar */}
										<div className="flex space-x-4 mb-6">
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
												</svg>
												Upload New Results
											</button>
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												Request New Imaging
											</button>
										</div>
										{/* Imaging and Results Content Grid */}
										<div className="grid grid-cols-3 gap-6">
											{/* Left Column - Studies List */}
											<div className="col-span-1">
												<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
													<h3 className="text-lg font-semibold text-base-content mb-4">Studies & Results</h3>

													{/* Search and Filter */}
													<div className="mb-4">
														<div className="relative">
															<input
																type="text"
																placeholder="Search studies..."
																className="w-full pl-10 pr-4 py-2 border border-base-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
															/>
															<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
																<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
																</svg>
															</div>
														</div>
													</div>

													{/* Studies List */}
													<div className="space-y-3">
														{/* Latest MRI */}
														<div className="p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer">
															<div className="flex items-start justify-between">
																<div>
																	<h4 className="text-sm font-medium text-base-content">Lumbar Spine MRI</h4>
																	<p className="text-xs text-base-content opacity-70">05/11/2024</p>
																	<p className="text-xs text-gray-500">City Imaging Center</p>
																</div>
																<span className="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
																	New
																</span>
															</div>
														</div>

														{/* X-Ray */}
														<div className="p-3 bg-base-100 border border-base-300 rounded-lg cursor-pointer">
															<div className="flex items-start justify-between">
																<div>
																	<h4 className="text-sm font-medium text-base-content">Lumbar X-Ray</h4>
																	<p className="text-xs text-base-content opacity-70">15/10/2024</p>
																	<p className="text-xs text-gray-500">Emergency Department</p>
																</div>
																<span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
																	Complete
																</span>
															</div>
														</div>

														{/* EMG Study */}
														<div className="p-3 bg-base-100 border border-base-300 rounded-lg cursor-pointer">
															<div className="flex items-start justify-between">
																<div>
																	<h4 className="text-sm font-medium text-base-content">EMG Study</h4>
																	<p className="text-xs text-base-content opacity-70">01/10/2024</p>
																	<p className="text-xs text-gray-500">Neurology Associates</p>
																</div>
																
																<span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
																	Complete
																</span>
															</div>
														</div>

														{/* Blood Work */}
														<div className="p-3 bg-base-100 border border-base-300 rounded-lg cursor-pointer">
															<div className="flex items-start justify-between">
																<div>
																	<h4 className="text-sm font-medium text-base-content">Blood Work Panel</h4>
																	<p className="text-xs text-base-content opacity-70">28/09/2024</p>
																	<p className="text-xs text-gray-500">PathLab Results</p>
																</div>
																<span className="px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
																	Complete
																</span>
															</div>
														</div>
													</div>
												</div>
											</div>

											{/* Center and Right Columns - Image Viewer and Report */}
											<div className="col-span-2 space-y-6">
												{/* Image Viewer */}
												<div className="bg-base-100 rounded-lg shadow border border-base-300 dark:border-gray-700">
													{/* Cornerstone/DICOM viewer temporarily disabled — too slow to build (see TE-27). */}
													<p className="p-6 text-center text-base-content opacity-60">DICOM viewer temporarily unavailable</p>
												</div>

												{/* Radiology Report */}
												<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
													<div className="flex justify-between items-start mb-4">
														<div>
															<h3 className="text-lg font-semibold text-base-content">Radiology Report</h3>
															<p className="text-sm text-gray-500">
																Report Date: 06/11/2024 | Radiologist: Dr. James Wilson
															</p>
														</div>
														<button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200">
															Download PDF
														</button>
													</div>

													<div className="space-y-4">
														<div>
															<h4 className="text-sm font-medium text-base-content mb-1">Clinical Information</h4>
															<p className="text-sm text-base-content opacity-70">
																45-year-old female with chronic lower back pain and left-sided radiculopathy. History of workplace injury.
															</p>
														</div>
														<div>
															<h4 className="text-sm font-medium text-base-content mb-1">Technique</h4>
															<p className="text-sm text-base-content opacity-70">
																Multiplanar multisequence MRI of the lumbar spine performed with and without contrast enhancement.
															</p>
														</div>

														<div>
															<h4 className="text-sm font-medium text-base-content mb-1">Findings</h4>
															<div className="space-y-2 text-sm text-base-content opacity-70">
																<p><span className="font-medium">Alignment:</span> Normal lordotic curvature maintained.</p>
																<p><span className="font-medium">Vertebral Bodies:</span> Normal height and signal intensity.</p>
																<p><span className="font-medium">L4-L5:</span> Large left paracentral disc extrusion with inferior migration, causing severe left lateral recess stenosis and compression of the traversing left L5 nerve root.</p>
																<p><span className="font-medium">L5-S1:</span> Mild broad-based disc bulge without significant neural compression.</p>
																<p><span className="font-medium">Conus Medullaris:</span> Normal in position and signal.</p>
																<p><span className="font-medium">Paraspinal Soft Tissues:</span> Unremarkable.</p>
															</div>
														</div>

														<div>
															<h4 className="text-sm font-medium text-base-content mb-1">Impression</h4>
															<ol className="list-decimal list-inside space-y-1 text-sm text-base-content opacity-70">
																<li>Large left paracentral disc extrusion at L4-L5 with inferior migration causing severe left lateral recess stenosis and left L5 nerve root compression.</li>
																<li>Mild disc bulge at L5-S1 without significant neural compression.</li>
															</ol>
														</div>

														{/* Key Images */}
														<div className="border-t border-base-300 pt-4 mt-4">
															<h4 className="text-sm font-medium text-base-content mb-2">Key Images</h4>
															<div className="grid grid-cols-4 gap-4">
																{[1, 2, 3, 4].map((img) => (
																	<div key={img} className="relative group">
																		<div className="aspect-w-1 aspect-h-1 bg-base-200 rounded-lg overflow-hidden">
																			<div className="absolute inset-0 flex items-center justify-center text-gray-500 group-hover:bg-gray-300">
																				Image {img}
																			</div>
																		</div>
																		<button className="absolute top-2 right-2 p-1 bg-base-100 rounded-full shadow opacity-0 group-hover:opacity-100">
																			<svg className="h-4 w-4 text-base-content opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																			</svg>
																		</button>
																	</div>
																))}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}
								{/* Documents Tab Content */}
								{activeTab === 'documents' && (
									<div className="space-y-6">
										<p className="text-sm text-base-content opacity-60">{documents.length} document{documents.length !== 1 ? 's' : ''} on file</p>
										{/* Quick Actions Bar */}
										<div className="flex flex-wrap gap-4 mb-6">
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
												</svg>
												Upload Document
											</button>
											<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												Create New Document
											</button>
											<div className="flex-1">
												<div className="relative">
													<input
														type="text"
														placeholder="Search documents..."
														className="w-full pl-10 pr-4 py-2 border border-base-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
													/>
													<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
														<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
														</svg>
													</div>
												</div>
											</div>
										</div>
										{/* Documents Grid Layout */}
										<div className="grid grid-cols-4 gap-6">
											{/* Left Sidebar - Categories */}
											<div className="col-span-1">
												<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
													<h3 className="text-lg font-semibold text-base-content mb-4">Categories</h3>

													<div className="space-y-2">
														<button className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg">
															<span className="flex items-center">
																<svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																</svg>
																Clinical Notes
															</span>
															<span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
																12
															</span>
														</button>

														<button className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-base-content opacity-80 hover:bg-base-200 rounded-lg">
															<span className="flex items-center">
																<svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
																</svg>
																Referral Letters
															</span>
															<span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
																8
															</span>
														</button>

														<button className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-base-content opacity-80 hover:bg-base-200 rounded-lg">
															<span className="flex items-center">
																<svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																</svg>
																Test Results
															</span>
															<span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
																15
															</span>
														</button>

														<button className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-base-content opacity-80 hover:bg-base-200 rounded-lg">
															<span className="flex items-center">
																<svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
																</svg>
																Imaging Reports
															</span>
															<span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
																6
															</span>
														</button>

														<button className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-base-content opacity-80 hover:bg-base-200 rounded-lg">
															<span className="flex items-center">
																<svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
																</svg>
																Insurance Documents
															</span>
															<span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
																4
															</span>
														</button>
													</div>

													{/* Tags Section */}
													<div className="mt-6">
														<h4 className="text-sm font-medium text-base-content mb-3">Tags</h4>
														<div className="flex flex-wrap gap-2">
															<span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
																#urgent
															</span>
															<span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
																#reviewed
															</span>
															<span className="px-3 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
																#pending
															</span>
															<span className="px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
																#followup
															</span>
														</div>
													</div>
												</div>
											</div>
											{/* Main Documents List */}
											<div className="col-span-2">
												<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
													<div className="flex justify-between items-center mb-4">
														<h3 className="text-lg font-semibold text-base-content">Clinical Notes</h3>
														<div className="flex items-center space-x-2">
															<button className="p-2 text-base-content opacity-70 hover:text-base-content rounded-lg hover:bg-gray-100">
																<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
																</svg>
															</button>
															<button className="p-2 text-base-content opacity-70 hover:text-base-content rounded-lg hover:bg-gray-100">
																<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
																</svg>
															</button>
														</div>
													</div>

													{/* Documents List */}
													<div className="space-y-3">
														{/* New Clinical Note */}
														<div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
															<div className="flex items-start justify-between">
																<div className="flex-1">
																	<div className="flex items-center">
																		<svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																		</svg>
																		<h4 className="text-sm font-medium text-base-content">Follow-up Assessment Note</h4>
																	</div>
																	<p className="mt-1 text-sm text-base-content opacity-70">Pain management review and treatment plan update</p>
																	<div className="mt-2 flex items-center space-x-4">
																		<span className="text-xs text-gray-500">Created: Today 10:30 AM</span>
																		<span className="text-xs text-gray-500">By: Dr. Smith</span>
																		<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
																			New
																		</span>
																	</div>
																</div>
																<div className="flex space-x-2">
																	<button className="p-2 text-blue-600 hover:text-primary rounded-lg hover:bg-blue-100">
																		<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																		</svg>
																	</button>
																	<button className="p-2 text-blue-600 hover:text-primary rounded-lg hover:bg-blue-100">
																		<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4-4m4 4V4" />
																		</svg>
																	</button>
																</div>
															</div>
														</div>

														{/* Previous Clinical Notes */}
														<div className="p-4 bg-base-100 border border-base-300 rounded-lg">
															<div className="flex items-start justify-between">
																<div className="flex-1">
																	<div className="flex items-center">
																		<svg className="h-5 w-5 text-base-content opacity-70 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																		</svg>
																		<h4 className="text-sm font-medium text-base-content">Initial Pain Assessment</h4>
																	</div>
																	<p className="mt-1 text-sm text-base-content opacity-70">Comprehensive pain evaluation and initial treatment plan</p>
																	<div className="mt-2 flex items-center space-x-4">
																		<span className="text-xs text-gray-500">Created: 01/11/2024</span>
																		<span className="text-xs text-gray-500">By: Dr. Smith</span>
																		<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
																			Reviewed
																		</span>
																	</div>
																</div>
																<div className="flex space-x-2">
																	<button className="p-2 text-base-content opacity-70 hover:text-base-content opacity-80 rounded-lg hover:bg-gray-100">
																		<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																		</svg>
																	</button>
																	<button className="p-2 text-base-content opacity-70 hover:text-base-content opacity-80 rounded-lg hover:bg-gray-100">
																		<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4-4m4 4V4" />
																		</svg>
																	</button>
																</div>
															</div>
														</div>

														{/* Referral Letters */}
														<div className="p-4 bg-base-100 border border-base-300 rounded-lg">
															<div className="flex items-start justify-between">
																<div className="flex-1">
																	<div className="flex items-center">
																		<svg className="h-5 w-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
																		</svg>
																		<h4 className="text-sm font-medium text-base-content">Neurosurgery Referral</h4>
																	</div>
																	<p className="mt-1 text-sm text-base-content opacity-70">Consultation request for surgical evaluation</p>
																	<div className="mt-2 flex items-center space-x-4">
																		<span className="text-xs text-gray-500">Created: 28/10/2024</span>
																		<span className="text-xs text-gray-500">By: Dr. Smith</span>
																		<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
																			Pending Response
																		</span>
																	</div>
																</div>
																<div className="flex space-x-2">
																	<button className="p-2 text-base-content opacity-70 hover:text-base-content opacity-80 rounded-lg hover:bg-gray-100">
																		<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																		</svg>
																	</button>
																	<button className="p-2 text-base-content opacity-70 hover:text-base-content opacity-80 rounded-lg hover:bg-gray-100">
																		<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0l-4-4m4 4V4" />
																		</svg>
																	</button>
																</div>
															</div>
														</div>
														{/* Document Preview Panel */}
														<div className="col-span-1">
															<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300 dark:border-gray-700">
																<div className="flex justify-between items-center mb-4">
																	<h3 className="text-lg font-semibold text-base-content">Document Preview</h3>
																	<div className="flex space-x-2">
																		<button className="p-2 text-base-content opacity-70 hover:text-base-content rounded-lg hover:bg-gray-100">
																			<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0L8 8m4-4v12" />
																			</svg>
																		</button>
																		<button className="p-2 text-base-content opacity-70 hover:text-base-content rounded-lg hover:bg-gray-100">
																			<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
																			</svg>
																		</button>
																	</div>
																</div>
																<div className="h-[calc(100vh-24rem)] overflow-y-auto">
																	<div className="prose prose-sm max-w-none">
																		{/* Document content would go here */}
																		<h4>Follow-up Assessment Note</h4>
																		<p className="text-sm text-base-content opacity-70">Date: 10/11/2024</p>
																		<p className="text-sm text-base-content opacity-70">Author: Dr. Smith</p>
																		<hr className="my-4" />
																		<div className="space-y-4">
																			<p>Patient presents for follow-up of chronic lower back pain...</p>
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Correspondence Tab Content */}
								{activeTab === 'correspondence' && (
									<div className="space-y-6">
										<p className="text-sm text-base-content opacity-60">{correspondence.length} item{correspondence.length !== 1 ? 's' : ''} of correspondence on file</p>
										{/* Quick Actions and Filters */}
										<div className="flex flex-wrap items-center justify-between gap-4">
											<div className="flex items-center space-x-4">
												<button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
													<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
													</svg>
													New Message
												</button>
												<div className="flex items-center space-x-2 bg-base-100 rounded-lg shadow-sm border border-base-300 p-2">
													<div className="flex items-center space-x-2">
														<input
															type="radio"
															id="all-correspondence"
															name="correspondence-direction"
															value="all"
															className="w-4 h-4 text-blue-600 border-base-300 focus:ring-blue-500"
															defaultChecked
														/>
														<label htmlFor="all-correspondence" className="text-sm text-base-content opacity-80">All</label>
													</div>
													<div className="flex items-center space-x-2">
														<input
															type="radio"
															id="inbound-correspondence"
															name="correspondence-direction"
															value="inbound"
															className="w-4 h-4 text-blue-600 border-base-300 focus:ring-blue-500"
														/>
														<label htmlFor="inbound-correspondence" className="text-sm text-base-content opacity-80">Inbound</label>
													</div>
													<div className="flex items-center space-x-2">
														<input
															type="radio"
															id="outbound-correspondence"
															name="correspondence-direction"
															value="outbound"
															className="w-4 h-4 text-blue-600 border-base-300 focus:ring-blue-500"
														/>
														<label htmlFor="outbound-correspondence" className="text-sm text-base-content opacity-80">Outbound</label>
													</div>
												</div>
											</div>

											<div className="flex-1 max-w-md">
												<div className="relative">
													<input
														type="text"
														placeholder="Search correspondence..."
														className="w-full pl-10 pr-4 py-2 border border-base-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
													/>
													<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
														<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
														</svg>
													</div>
												</div>
											</div>
										</div>

										{/* Correspondence List */}
										<div className="bg-base-100 rounded-lg shadow">
											{/* Message Type Legend */}
											<div className="px-4 py-3 border-b border-base-300 dark:border-gray-700">
												<div className="flex items-center space-x-4 text-sm">
													<span className="flex items-center">
														
														HealthLink
													</span>
													<span className="flex items-center">
														
														Argus
													</span>
													<span className="flex items-center">
														
														MedicalObjects
													</span>
												</div>
											</div>

											{/* Messages List */}
											<div className="divide-y divide-base-300 dark:divide-gray-700">
												{/* Inbound Message Example */}
												<div className="p-4 hover:bg-base-200 dark:hover:bg-gray-700">
													<div className="flex items-start space-x-4">
														<div className="flex-shrink-0">
															<span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-green-100 text-green-500">
																<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
																</svg>
															</span>
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex items-center justify-between">
																<h4 className="text-sm font-medium text-base-content">Dr. James Wilson - Neurosurgery Report</h4>
																<div className="flex items-center">
																	<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
																		HealthLink
																	</span>
																	<span className="ml-2 text-sm text-gray-500">10:30 AM</span>
																</div>
															</div>
															<p className="mt-1 text-sm text-base-content opacity-70 line-clamp-2">
																Post-consultation report regarding surgical options for L4-L5 disc herniation...
															</p>
															<div className="mt-2 flex items-center space-x-4">
																<span className="text-xs text-gray-500">Message ID: HL7-2024-1105-001</span>
																<span className="inline-flex items-center text-xs text-green-600">
																	<svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
																	</svg>
																	Received & Processed
																</span>
															</div>
														</div>
														<div className="flex-shrink-0 ml-4">
															<button className="p-2 text-base-content opacity-70 hover:text-base-content rounded-lg hover:bg-gray-100">
																<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																</svg>
															</button>
														</div>
													</div>
												</div>

												{/* Outbound Message Example */}
												<div className="p-4 hover:bg-base-200 dark:hover:bg-gray-700">
													<div className="flex items-start space-x-4">
														<div className="flex-shrink-0">
															<span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-500">
																<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
																</svg>
															</span>
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex items-center justify-between">
																<h4 className="text-sm font-medium text-base-content">Referral to Dr. Sarah Chen - Pain Management</h4>
																<div className="flex items-center">
																	<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
																		Argus
																	</span>
																	<span className="ml-2 text-sm text-gray-500">Yesterday</span>
																</div>
															</div>
															<p className="mt-1 text-sm text-base-content opacity-70 line-clamp-2">
																Referral for second opinion regarding complex regional pain syndrome...
															</p>
															<div className="mt-2 flex items-center space-x-4">
																<span className="text-xs text-gray-500">Message ID: ARG-2024-1104-003</span>
																<span className="inline-flex items-center text-xs text-blue-600">
																	<svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
																	</svg>
																	Delivered
																</span>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Billing Tab Content */}
								{activeTab === 'billing' && (
									<div className="space-y-6">
										{/* Top Row - Account Overview */}
										<div className="grid grid-cols-2 gap-6">
											{/* Account Standing */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<div className="flex justify-between items-start">
													<div>
														<h3 className="text-lg font-semibold text-base-content">Account Standing</h3>
														<div className="flex items-center space-x-2 mt-2">
															<span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
																<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
																</svg>
																Attention Required
															</span>
															<span className="text-sm text-gray-500">3 Overdue Bills</span>
														</div>
													</div>
													<span className="text-lg font-semibold text-red-600">$850.00 Outstanding</span>
												</div>

												<div className="mt-4">
													<div className="w-full bg-base-200 rounded-full h-2">
														<div className="bg-red-600 h-2 rounded-full" style={{ width: '45%' }}></div>
													</div>
												</div>
											</div>

											{/* Payment Methods */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<div className="flex justify-between items-center mb-4">
													<h3 className="text-lg font-semibold text-base-content">Payment Methods</h3>
													<button className="text-sm text-blue-600 hover:text-primary">Add New</button>
												</div>
												<div className="space-y-3">
													<div className="flex items-center p-3 bg-gray-50 rounded-lg">
														<svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
														</svg>
														<div>
															<p className="text-sm font-medium text-base-content">VISA ending in 4242</p>
															<p className="text-xs text-gray-500">Expires 12/25</p>
														</div>
														<span className="ml-auto px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">Default</span>
													</div>

													<div className="flex items-center p-3 bg-gray-50 rounded-lg">
														<svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
														</svg>
														<div>
															<p className="text-sm font-medium text-base-content">Medicare Card</p>
															<p className="text-xs text-gray-500">2937-28376-1</p>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Recent Invoices */}
										<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
											<div className="flex justify-between items-center mb-4">
												<h3 className="text-lg font-semibold text-base-content">Recent Invoices</h3>
												<button className="text-sm text-blue-600 hover:text-primary">View All</button>
											</div>

											<div className="overflow-x-auto">
												<table className="w-full">
													<thead>
														<tr className="text-left text-sm text-gray-500 border-b">
															<th className="pb-3 font-medium">Invoice #</th>
															<th className="pb-3 font-medium">Date</th>
															<th className="pb-3 font-medium">Description</th>
															<th className="pb-3 font-medium">Amount</th>
															<th className="pb-3 font-medium">Status</th>
															<th className="pb-3 font-medium">Actions</th>
														</tr>
													</thead>
													<tbody className="divide-y">
														<tr className="text-sm">
															<td className="py-3">INV-2024-001</td>
															<td className="py-3">10/11/2024</td>
															<td className="py-3">Initial Consultation</td>
															<td className="py-3">$250.00</td>
															<td className="py-3">
																<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
																	Overdue
																</span>
															</td>
															<td className="py-3">
																<button className="text-blue-600 hover:text-primary">Pay Now</button>
															</td>
														</tr>
														{/* Add more rows as needed */}
													</tbody>
												</table>
											</div>
										</div>

										{/* Bottom Row */}
										<div className="grid grid-cols-2 gap-6">
											{/* Unpaid Bills */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<div className="flex items-center justify-between mb-4">
													<h3 className="text-lg font-semibold text-base-content">Unpaid Bills</h3>
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
														3 Outstanding
													</span>
												</div>

												<div className="space-y-3">
													<div className="p-3 bg-red-50 border border-red-100 rounded-lg">
														<div className="flex justify-between items-start">
															<div>
																<p className="text-sm font-medium text-base-content">Initial Consultation</p>
																<p className="text-xs text-gray-500">Due: 25/10/2024</p>
															</div>
															<span className="text-sm font-semibold text-red-600">$250.00</span>
														</div>
														<div className="mt-2 flex items-center justify-between">
															<span className="text-xs text-red-600">25 Days Overdue</span>
															<button className="text-sm text-blue-600 hover:text-primary">Pay Now</button>
														</div>
													</div>
													{/* Add more unpaid bills */}
												</div>
											</div>

											{/* Upcoming Procedures */}
											<div className="bg-base-100 rounded-lg shadow p-6 border border-base-300">
												<div className="flex items-center justify-between mb-4">
													<h3 className="text-lg font-semibold text-base-content">Upcoming Procedures</h3>
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
														2 Scheduled
													</span>
												</div>

												<div className="space-y-3">
													<div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
														<div className="flex justify-between items-start">
															<div>
																<p className="text-sm font-medium text-base-content">MRI Lumbar Spine</p>
																<p className="text-xs text-gray-500">22/11/2024 - 9:15 AM</p>
																<p className="text-xs text-gray-500">City Imaging Center</p>
															</div>
															<span className="text-sm font-semibold text-blue-600">Est. $550.00</span>
														</div>
														<div className="mt-2">
															<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
																Medicare Eligible
															</span>
														</div>
													</div>
													{/* Add more upcoming procedures */}
												</div>
											</div>
										</div>
									</div>
								)}

								{activeTab === 'referrals' && (
									<div className="space-y-6">
										{/* Header with Add Button */}
										<div className="flex justify-between items-center">
											<div className="flex items-center space-x-4">
												<h3 className="text-lg font-semibold text-base-content">Active Referrals</h3>
												<div className="flex gap-2">
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
														2 Active
													</span>
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
														1 Expiring Soon
													</span>
												</div>
											</div>
											<button
												className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
												onClick={handleAddReferral}
											>
												<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
												</svg>
												New Referral
											</button>
										</div>

										{/* Quick Filters */}
										<div className="flex items-center space-x-4 bg-base-100 p-4 rounded-lg shadow border border-base-300">
											<div className="flex items-center space-x-2">
												<input
													type="radio"
													id="all-referrals"
													name="referral-filter"
													value="all"
													className="w-4 h-4 text-blue-600 border-base-300 focus:ring-blue-500"
													defaultChecked
												/>
												<label htmlFor="all-referrals" className="text-sm text-base-content opacity-80">All</label>
											</div>
											<div className="flex items-center space-x-2">
												<input
													type="radio"
													id="active-referrals"
													name="referral-filter"
													value="active"
													className="w-4 h-4 text-blue-600 border-base-300 focus:ring-blue-500"
												/>
												<label htmlFor="active-referrals" className="text-sm text-base-content opacity-80">Active Only</label>
											</div>
											<div className="flex items-center space-x-2">
												<input
													type="radio"
													id="expired-referrals"
													name="referral-filter"
													value="expired"
													className="w-4 h-4 text-blue-600 border-base-300 focus:ring-blue-500"
												/>
												<label htmlFor="expired-referrals" className="text-sm text-base-content opacity-80">Expired</label>
											</div>
											<div className="ml-auto">
												<div className="relative">
													<input
														type="text"
														placeholder="Search referrals..."
														className="w-64 pl-10 pr-4 py-2 border border-base-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
													/>
													<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
														<svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
														</svg>
													</div>
												</div>
											</div>
										</div>

										{/* Referrals Table */}
										<div className="bg-base-100 shadow rounded-lg overflow-hidden">
											<table className="min-w-full divide-y divide-base-300">
												<thead className="bg-gray-50">
													<tr>
														<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Start Date
														</th>
														<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Date of Referral
														</th>
														<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Receiving Doctor
														</th>
														<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Referrer
														</th>
														<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Duration
														</th>
														<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Status
														</th>
														<th scope="col" className="relative px-6 py-3">
															<span className="sr-only">Actions</span>
														</th>
													</tr>
												</thead>
												<tbody className="bg-base-100 divide-y divide-base-300">
													{/* Active Referral Example */}
													<tr>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">28/03/2017</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">28/03/2017</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">Dr. Sarah Chen</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<div className="text-sm text-base-content">Mr Greg Malham</div>
															<div className="text-sm text-gray-500">
																Work: 03 9866 6650
																<br />
																Mobile: 0407 818 848
															</div>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">3 Months</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
																Valid
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
															<button className="text-blue-600 hover:text-blue-900 mr-4">View</button>
															<button className="text-blue-600 hover:text-blue-900">Edit</button>
														</td>
													</tr>

													{/* Expiring Soon Example */}
													<tr className="bg-yellow-50">
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">15/01/2024</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">15/01/2024</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">Dr. James Wilson</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<div className="text-sm text-base-content">Dr. Robert Smith</div>
															<div className="text-sm text-gray-500">
																Work: 03 9555 7777
															</div>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">3 Months</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
																Expires in 5 days
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
															<button className="text-blue-600 hover:text-blue-900 mr-4">View</button>
															<button className="text-blue-600 hover:text-blue-900">Renew</button>
														</td>
													</tr>
												</tbody>
											</table>
										</div>

										{/* Pagination */}
										<div className="flex items-center justify-between px-4 py-3 border-t border-base-300 sm:px-6">
											<div className="flex-1 flex justify-between sm:hidden">
												<button className="relative inline-flex items-center px-4 py-2 border border-base-300 text-sm font-medium rounded-md text-base-content opacity-80 bg-base-100 hover:bg-base-200">
													Previous
												</button>
												<button className="ml-3 relative inline-flex items-center px-4 py-2 border border-base-300 text-sm font-medium rounded-md text-base-content opacity-80 bg-base-100 hover:bg-base-200">
													Next
												</button>
											</div>
											<div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
												<div>
													<p className="text-sm text-base-content opacity-80">
														Showing <span className="font-medium">1</span> to <span className="font-medium">2</span> of{' '}
														<span className="font-medium">2</span> results
													</p>
												</div>
												<div>
													<nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
														<button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-base-300 bg-base-100 text-sm font-medium text-gray-500 hover:bg-base-200">
															<span className="sr-only">Previous</span>
															<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
																<path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
															</svg>
														</button>
														<button className="relative inline-flex items-center px-4 py-2 border border-base-300 bg-base-100 text-sm font-medium text-base-content opacity-80 hover:bg-base-200">
															1
														</button>
														<button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-base-300 bg-base-100 text-sm font-medium text-gray-500 hover:bg-base-200">
															<span className="sr-only">Next</span>
															<svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
																<path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
															</svg>
														</button>
													</nav>
												</div>
											</div>
										</div>
									</div>
								)}

								{activeTab === 'insurance' && (
									<div className="space-y-6">
										{/* Insurance Policies Section */}
										<div>
											<div className="flex justify-between items-center mb-6">
												<div className="flex items-center space-x-4">
													<h3 className="text-lg font-semibold text-base-content">Insurance Policies</h3>
													<div className="flex gap-2">
														<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
															{patient.insurance?.length || 0} Active Policies
														</span>
													</div>
												</div>
												<button
													className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
													onClick={handleAddInsurance}
												>
													<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
													</svg>
													Add New Policy
												</button>
											</div>

											{/* Insurance Table */}
											<div className="bg-base-100 shadow rounded-lg overflow-hidden">
												<table className="min-w-full divide-y divide-base-300">
													<thead className="bg-gray-50">
														<tr>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Insurance Type
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Policy Number
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Provider
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Membership Number
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Valid Period
															</th>
															<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																Status
															</th>
															<th scope="col" className="relative px-6 py-3">
																<span className="sr-only">Actions</span>
															</th>
														</tr>
													</thead>
													<tbody className="divide-y divide-base-300">
														{patient.insurance && patient.insurance.length > 0 ? (
															patient.insurance.map((insurance) => (
																<tr key={insurance.id}>
																	<td className="px-6 py-4 whitespace-nowrap">
																		<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInsuranceColor(insurance.insurance_type).bg} ${getInsuranceColor(insurance.insurance_type).text}`}>
																			{insurance.insurance_type_display || insurance.insurance_type}
																		</span>
																	</td>
																	<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.policy_number || 'N/A'}</td>
																	<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.provider_name || 'N/A'}</td>
																	<td className="px-6 py-4 text-sm text-base-content">
																		{insurance.membership_number || 'N/A'}
																	</td>
																	<td className="px-6 py-4 whitespace-nowrap">
																		{insurance.expiry_date && (
																			<>
																				<div className="text-sm text-base-content">From: {insurance.created_at ? formatDate(insurance.created_at) : 'N/A'}</div>
																				<div className="text-sm text-gray-500">To: {formatDate(insurance.expiry_date)}</div>
																			</>
																		)}
																		{!insurance.expiry_date && (
																			<div className="text-sm text-gray-500">No expiry date</div>
																		)}
																	</td>
																	<td className="px-6 py-4 whitespace-nowrap">
																		<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
																			{insurance.is_primary ? 'Primary' : 'Active'}
																		</span>
																	</td>
																	<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
																		<button 
																			className="text-blue-600 hover:text-blue-900 mr-2"
																			onClick={() => handleEditInsurance(insurance)}
																		>
																			Edit
																		</button>
																		<button 
																			className="text-red-600 hover:text-red-900"
																			onClick={() => insurance.id && handleDeleteInsurance(insurance.id)}
																		>
																			Delete
																		</button>
																	</td>
																</tr>
															))
														) : (
															<tr>
																<td colSpan={7} className="px-6 py-4 text-center text-gray-500">
																	No insurance policies found
																</td>
															</tr>
														)}
													</tbody>
												</table>
											</div>
										</div>

										{/* WorkCover Claims Section */}
										<div className="mt-8">
											<div className="flex justify-between items-center mb-6">
												<div className="flex items-center space-x-4">
													<h3 className="text-lg font-semibold text-base-content">WorkCover Claims</h3>
													<div className="flex gap-2">
														<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
															{patient.insurance?.filter(ins => ins.insurance_type === 'workcover').length || 0} Active Claims
														</span>
													</div>
												</div>
												<button 
													className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
													onClick={() => handleAddSpecialInsurance('workcover')}
												>
													<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
													</svg>
													Add New Claim
												</button>
											</div>

											<div className="bg-base-100 shadow rounded-lg overflow-hidden">
												{(patient.insurance ?? []).filter(ins => ins.insurance_type === 'workcover').length > 0 ? (
													<table className="min-w-full divide-y divide-base-300">
														<thead className="bg-gray-50">
															<tr>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Claim ID
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Provider
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Policy Number
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Details
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Expiry
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Status
																</th>
																<th scope="col" className="relative px-6 py-3">
																	<span className="sr-only">Actions</span>
																</th>
															</tr>
														</thead>
														<tbody className="divide-y divide-base-300">
															{patient?.insurance
																?.filter(ins => ins.insurance_type === 'workcover')
																?.map((insurance) => (
																	<tr key={insurance.id}>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.policy_number || 'N/A'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.provider_name || 'N/A'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.membership_number || 'N/A'}</td>
																		<td className="px-6 py-4 text-sm text-base-content opacity-70">{insurance.notes || 'No details'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">
																			{insurance.expiry_date ? formatDate(insurance.expiry_date) : 'No expiry'}
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap">
																			<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
																				Active
																			</span>
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
																			<button 
																				className="text-blue-600 hover:text-blue-900 mr-2"
																				onClick={() => handleEditInsurance(insurance)}
																			>
																				Edit
																			</button>
																			<button 
																				className="text-red-600 hover:text-red-900"
																				onClick={() => insurance.id !== undefined && handleDeleteInsurance(insurance.id)}
																			>
																				Delete
																			</button>
																		</td>
																	</tr>
																))
															}
														</tbody>
													</table>
												) : (
													<div className="py-6 text-center text-gray-500">No WorkCover claims recorded</div>
												)}
											</div>
										</div>

										{/* Transport Accident Claims Section */}
										<div className="mt-8">
											<div className="flex justify-between items-center mb-6">
												<div className="flex items-center space-x-4">
													<h3 className="text-lg font-semibold text-base-content">Transport Accident Claims</h3>
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
														{patient.insurance?.filter(ins => ins.insurance_type === 'tac').length || 0} Claims
													</span>
												</div>
												<button 
													className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
													onClick={() => handleAddSpecialInsurance('tac')}
												>
													<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
													</svg>
													Add New TAC Claim
												</button>
											</div>

											<div className="bg-base-100 shadow rounded-lg overflow-hidden">
												{(patient.insurance || []).filter(ins => ins.insurance_type === 'tac').length > 0 ? (
													<table className="min-w-full divide-y divide-base-300">
														<thead className="bg-gray-50">
															<tr>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Claim ID
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Provider
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Reference Number
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Details
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Expiry
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Status
																</th>
																<th scope="col" className="relative px-6 py-3">
																	<span className="sr-only">Actions</span>
																</th>
															</tr>
														</thead>
														<tbody className="divide-y divide-base-300">
															{patient?.insurance
																?.filter(ins => ins.insurance_type === 'tac')
																?.map((insurance) => (
																	<tr key={insurance.id}>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.policy_number || 'N/A'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.provider_name || 'TAC'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.membership_number || 'N/A'}</td>
																		<td className="px-6 py-4 text-sm text-base-content opacity-70">{insurance.notes || 'No details'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">
																			{insurance.expiry_date ? formatDate(insurance.expiry_date) : 'No expiry'}
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap">
																			<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
																				Active
																			</span>
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
																			<button 
																				className="text-blue-600 hover:text-blue-900 mr-2"
																				onClick={() => handleEditInsurance(insurance)}
																			>
																				Edit
																			</button>
																			<button 
																				className="text-red-600 hover:text-red-900"
																				onClick={() => insurance.id !== undefined && handleDeleteInsurance(insurance.id)}
																			>
																				Delete
																			</button>
																		</td>
																	</tr>
																))
															}
														</tbody>
													</table>
												) : (
													<div className="py-6 text-center text-gray-500">No transport accident claims recorded</div>
												)}
											</div>
										</div>

										{/* DVA Claims Section */}
										<div className="mt-8">
											<div className="flex justify-between items-center mb-6">
												<div className="flex items-center space-x-4">
													<h3 className="text-lg font-semibold text-base-content">DVA Claims</h3>
													<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
														{patient.insurance?.filter(ins => ins.insurance_type === 'dva').length || 0} Claims
													</span>
												</div>
												<button 
													className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
													onClick={() => handleAddSpecialInsurance('dva')}
												>
													<svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
													</svg>
													Add New DVA Claim
												</button>
											</div>

											<div className="bg-base-100 shadow rounded-lg overflow-hidden">
												{(patient.insurance || []).filter(ins => ins.insurance_type === 'dva').length > 0 ? (
													<table className="min-w-full divide-y divide-base-300">
														<thead className="bg-gray-50">
															<tr>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Card Type
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	DVA Number
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Details
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Expiry
																</th>
																<th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																	Status
																</th>
																<th scope="col" className="relative px-6 py-3">
																	<span className="sr-only">Actions</span>
																</th>
															</tr>
														</thead>
														<tbody className="divide-y divide-base-300">
															{patient?.insurance
																?.filter(ins => ins.insurance_type === 'dva')
																?.map((insurance) => (
																	<tr key={insurance.id}>
																		<td className="px-6 py-4 whitespace-nowrap">
																			<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800`}>
																				DVA
																			</span>
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">{insurance.policy_number || 'N/A'}</td>
																		<td className="px-6 py-4 text-sm text-base-content opacity-70">{insurance.notes || 'No details'}</td>
																		<td className="px-6 py-4 whitespace-nowrap text-sm text-base-content">
																			{insurance.expiry_date ? formatDate(insurance.expiry_date) : 'No expiry'}
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap">
																			<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
																				Active
																			</span>
																		</td>
																		<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
																			<button 
																				className="text-blue-600 hover:text-blue-900 mr-2"
																				onClick={() => handleEditInsurance(insurance)}
																			>
																				Edit
																			</button>
																			<button 
																				className="text-red-600 hover:text-red-900"
																				onClick={() => insurance.id !== undefined && handleDeleteInsurance(insurance.id)}
																			>
																				Delete
																			</button>
																		</td>
																	</tr>
																))
															}
														</tbody>
													</table>
												) : (
													<div className="py-6 text-center text-gray-500">No DVA claims recorded</div>
												)}
											</div>
										</div>
									</div>
								)}
							</div>
					</div>
				</div>
			</div>
			
			{/* Add Insurance Modal */}
			{showAddInsuranceModal && (
				<div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
					<div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium">Add Insurance Policy</h3>
							<button
								onClick={() => setShowAddInsuranceModal(false)}
								className="text-gray-400 hover:text-gray-500"
							>
								<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						{currentInsurance && (
							<p className="text-sm text-base-content opacity-70 -mt-2 mb-2">
								{currentInsurance.insurance_type_display || currentInsurance.insurance_type}
								{currentInsurance.provider_name ? ` – ${currentInsurance.provider_name}` : ''}
							</p>
						)}

						{/* Insurance Form will go here */}
						<div className="mt-4 flex justify-end">
							<button
								onClick={() => setShowAddInsuranceModal(false)}
								className="mr-2 px-4 py-2 text-sm font-medium text-base-content opacity-80 bg-base-100 border border-base-300 rounded-md hover:bg-base-200"
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Insurance Modal */}
			{showEditInsuranceModal && (
				<div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
					<div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium">Edit Insurance Policy</h3>
							<button
								onClick={() => setShowEditInsuranceModal(false)}
								className="text-gray-400 hover:text-gray-500"
							>
								<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						{currentInsurance && (
							<p className="text-sm text-base-content opacity-70 -mt-2 mb-2">
								{currentInsurance.insurance_type_display || currentInsurance.insurance_type}
								{currentInsurance.provider_name ? ` – ${currentInsurance.provider_name}` : ''}
							</p>
						)}

						{/* Insurance Form will go here */}
						<div className="mt-4 flex justify-end">
							<button
								onClick={() => setShowEditInsuranceModal(false)}
								className="mr-2 px-4 py-2 text-sm font-medium text-base-content opacity-80 bg-base-100 border border-base-300 rounded-md hover:bg-base-200"
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
							>
								Update
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Add Admin Note Modal */}
			{showAddAdminNoteModal && (
				<div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
					<div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium">Add Admin Note</h3>
							<button 
								onClick={() => setShowAddAdminNoteModal(false)}
								className="text-gray-400 hover:text-gray-500"
							>
								<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>
						
						{/* Admin Note Form will go here */}
						<div className="mt-4 flex justify-end">
							<button
								onClick={() => setShowAddAdminNoteModal(false)}
								className="mr-2 px-4 py-2 text-sm font-medium text-base-content opacity-80 bg-base-100 border border-base-300 rounded-md hover:bg-base-200"
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Add Referral Modal */}
			{showAddReferralModal && (
				<div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50">
					<div className="bg-base-100 rounded-lg shadow-xl max-w-md w-full p-6">
						<div className="flex justify-between items-center mb-4">
							<h3 className="text-lg font-medium">New Referral</h3>
							<button
								onClick={() => setShowAddReferralModal(false)}
								className="text-gray-400 hover:text-gray-500"
							>
								<svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</div>

						{/* Referral Form will go here */}
						<div className="mt-4 flex justify-end">
							<button
								onClick={() => setShowAddReferralModal(false)}
								className="mr-2 px-4 py-2 text-sm font-medium text-base-content opacity-80 bg-base-100 border border-base-300 rounded-md hover:bg-base-200"
							>
								Cancel
							</button>
							<button
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Patient;

// Helper functions for insurance colors
function getInsuranceColor(insuranceType: string) {
	switch (insuranceType.toLowerCase()) {
		case 'private':
			return {
				bg: 'bg-orange-50',
				border: 'border-orange-200',
				dot: 'bg-orange-400',
				text: 'text-orange-800'
			};
		case 'dva':
			return {
				bg: 'bg-yellow-50',
				border: 'border-yellow-200',
				dot: 'bg-yellow-400',
				text: 'text-yellow-800'
			};
		case 'tac':
			return {
				bg: 'bg-blue-50',
				border: 'border-blue-200',
				dot: 'bg-blue-400',
				text: 'text-blue-800'
			};
		case 'workcover':
			return {
				bg: 'bg-green-50',
				border: 'border-green-200',
				dot: 'bg-green-400',
				text: 'text-green-800'
			};
		case 'medicare':
			return {
				bg: 'bg-teal-50',
				border: 'border-teal-200',
				dot: 'bg-teal-400',
				text: 'text-teal-800'
			};
		default:
			return {
				bg: 'bg-gray-50',
				border: 'border-base-300',
				dot: 'bg-gray-400',
				text: 'text-gray-800'
			};
	}
}

// Helper functions for status colors and icons
function getStatusColor(status: string) {
	switch (status) {
		case 'completed':
			return { text: 'text-green-700' };
		case 'overdue':
			return { text: 'text-red-700' };
		case 'in_progress':
			return { text: 'text-blue-700' };
		case 'pending':
			return { text: 'text-yellow-700' };
		default:
			return { text: 'text-base-content opacity-80' };
	}
}

function getStatusIcon(status: string) {
	switch (status) {
		case 'completed':
			return (
				<svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
				</svg>
			);
		case 'overdue':
		case 'pending':
			return (
				<svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
				</svg>
			);
		case 'in_progress':
			return (
				<svg className="mr-1 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
				</svg>
			);
		default:
			return null;
	}
}

function capitalize(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}
